import PropTypes from 'prop-types';
import { Button as MuiButton } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import './Button.css';

/**
 * Reusable Button Component (Material UI wrapper)
 *
 * @param {String} variant - Button style: "primary", "secondary", "danger", "success", "outline" (default: "primary")
 * @param {String} size - Button size: "sm", "md", "lg" (default: "md")
 * @param {Boolean} disabled - Disable button (default: false)
 * @param {Boolean} visible - Show/hide button (default: true)
 * @param {Boolean} fullWidth - Make button full width (default: false)
 * @param {String} icon - Icon name: "add", "delete", "edit", "save", "close", "check" or FontAwesome class
 * @param {String} iconPosition - Icon position: "left", "right" (default: "left")
 * @param {Function} onClick - Click handler
 * @param {String} type - Button type: "button", "submit", "reset" (default: "button")
 * @param {String} className - Additional CSS classes
 * @param {React.ReactNode} children - Button content
 */
function Button({
  variant = 'primary',
  size = 'md',
  disabled = false,
  visible = true,
  fullWidth = false,
  icon,
  iconPosition = 'left',
  onClick,
  type = 'button',
  className = '',
  children,
  ...props
}) {
  // Don't render if not visible
  if (!visible) {
    return null;
  }

  // Map custom variant to MUI variant and color
  const getMuiProps = () => {
    switch (variant) {
      case 'primary':
        return { variant: 'contained', color: 'primary' };
      case 'secondary':
        return { variant: 'contained', color: 'secondary' };
      case 'danger':
        return { variant: 'contained', color: 'error' };
      case 'success':
        return { variant: 'contained', color: 'success' };
      case 'outline':
        return { variant: 'outlined', color: 'primary' };
      default:
        return { variant: 'contained', color: 'primary' };
    }
  };

  // Map size to MUI size
  const getMuiSize = () => {
    switch (size) {
      case 'sm':
        return 'small';
      case 'lg':
        return 'large';
      case 'md':
      default:
        return 'medium';
    }
  };

  // Get Material UI icon component
  const getIconComponent = () => {
    if (!icon) return null;

    switch (icon) {
      case 'add':
        return <AddIcon />;
      case 'delete':
        return <DeleteIcon />;
      case 'edit':
        return <EditIcon />;
      case 'save':
        return <SaveIcon />;
      case 'close':
        return <CloseIcon />;
      case 'check':
        return <CheckIcon />;
      default:
        // Fallback to FontAwesome icon if provided
        return icon.includes('fa-') ? <i className={icon}></i> : null;
    }
  };

  const iconComponent = getIconComponent();
  const muiProps = getMuiProps();

  return (
    <MuiButton
      type={type}
      onClick={onClick}
      disabled={disabled}
      fullWidth={fullWidth}
      size={getMuiSize()}
      className={className}
      startIcon={iconPosition === 'left' ? iconComponent : undefined}
      endIcon={iconPosition === 'right' ? iconComponent : undefined}
      {...muiProps}
      {...props}
    >
      {children}
    </MuiButton>
  );
}

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'success', 'outline']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  visible: PropTypes.bool,
  fullWidth: PropTypes.bool,
  icon: PropTypes.string,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Button;
